import { useEffect, useState } from "preact/hooks";
import { parsePhoneNumber } from "libphonenumber-js";
import QRCode from "react-qr-code";
import * as ReactSpinners from "react-spinners";

const ClipLoader = (ReactSpinners.ClipLoader ||
  (ReactSpinners as unknown as { default: typeof ReactSpinners.ClipLoader })
    .default) as typeof ReactSpinners.ClipLoader;
import { toast } from "react-hot-toast";
import { Button } from "./button.tsx";
export { Button } from "./button.tsx";

export const ErrorMessage = ({ error }: { error: { message: string } }) => {
  return (
    <div
      style={{
        display: "flex",
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        gap: 5,
      }}
    >
      <div>
        Uh oh. An error has occurred. Try again later or talk to us.
      </div>
      <code>{error.message}</code>
    </div>
  );
};

export const LoadingIndication = (_props?: { text?: string }) => (
  <div
    style={{
      display: "flex",
      flexGrow: 1,
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
      minHeight: "60vh",
    }}
  >
    <ClipLoader color="white" size={36} />
  </div>
);

export const QrCode = ({ url, size }: { size: number; url: string }) => (
  // @ts-expect-error lib has not typing
  <QRCode
    size={size}
    style={{ border: "solid 1px white" }}
    value={url}
  />
);

type QrWithTimerProps = {
  url: string;
  size: number;
  issuedAt: number;
};

const useCountdownLeft = (issuedAt: number, durationSec: number) => {
  const computeLeft = () => {
    if (!issuedAt) return durationSec;
    const elapsed = Math.floor((Date.now() - issuedAt) / 1000);
    return Math.max(0, Math.min(durationSec, durationSec - elapsed));
  };

  const [left, setLeft] = useState<number>(computeLeft());

  useEffect(() => {
    // Recompute immediately and set an anchored ticking interval
    setLeft(computeLeft());
    const id = setInterval(() => {
      setLeft(computeLeft());
    }, 1000);
    return () => clearInterval(id);
    // react to issuedAt/duration changes so a new code resets the countdown
  }, [issuedAt, durationSec]);

  return left;
};

const TimerBar = (
  { left, durationSec, width }: {
    left: number;
    durationSec: number;
    width: number;
  },
) => {
  const pct = Math.max(0, Math.min(100, (left / durationSec) * 100));
  return (
    <div
      style={{
        height: 4,
        width,
        background: "rgba(255,255,255,0.15)",
        borderRadius: 2,
        overflow: "hidden",
        marginTop: 4,
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${pct}%`,
          background: "var(--primary)",
          transition: "width 0.2s linear",
        }}
      />
    </div>
  );
};

const expiredStyle = { opacity: 0.35, filter: "grayscale(1)" };

export const PairingCodeWithTimer = (
  { code, issuedAt, durationSec = 240 }: {
    code: string;
    issuedAt: number;
    durationSec?: number;
  },
) => {
  const left = useCountdownLeft(issuedAt, durationSec);
  const expired = left <= 0;
  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <div
        style={{
          fontSize: 32,
          fontWeight: 600,
          letterSpacing: 8,
          padding: "20px",
          border: "2px solid var(--border)",
          borderRadius: "var(--radius)",
          background: "var(--surface-alt)",
          textAlign: "center",
          fontFamily: "monospace",
          ...(expired ? expiredStyle : {}),
        }}
      >
        {code}
      </div>
      <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
        {expired ? "Expired — request a fresh code" : `Valid for ${left}s`}
      </div>
      <TimerBar left={left} durationSec={durationSec} width={220} />
    </div>
  );
};

export const QrWithTimer = ({ url, size, issuedAt }: QrWithTimerProps) => {
  const durationSec = 60;
  const left = useCountdownLeft(issuedAt, durationSec);
  const expired = left <= 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        ...(expired ? expiredStyle : {}),
      }}
    >
      <QrCode size={size} url={url} />
      <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
        {expired
          ? "Expired — waiting for a fresh code…"
          : `Refreshes in ${left}s`}
      </div>
      <TimerBar left={left} durationSec={durationSec} width={size} />
    </div>
  );
};

type SecretTokenProps = {
  token: string;
};

export const SecretToken = ({ token }: SecretTokenProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const hiddenPillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "var(--surface-alt)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    padding: "2px 8px",
    fontSize: 12,
    fontFamily: "inherit",
    letterSpacing: "normal",
  } as const;

  return (
    <div
      style={{
        fontSize: 14,
        opacity: 0.85,
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <span>Secret Token:</span>
      {isVisible
        ? (
          <span
            style={{
              fontFamily: "monospace",
              letterSpacing: "normal",
              // Avoid pushing action buttons to the far right
              // by not allowing the token text to flex-grow
              // Keep it shrinkable with ellipsis
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {token}
          </span>
        )
        : <span style={hiddenPillStyle}>🔒 Hidden</span>}
      <div style={{ display: "inline-flex", gap: 6 }}>
        <Button
          className="secondary"
          style={{ fontSize: 10, padding: "2px 6px" }}
          onClick={() => setIsVisible(!isVisible)}
        >
          {isVisible ? "🙈 Hide" : "👁 Show"}
        </Button>
        <Button
          className="secondary"
          style={{ fontSize: 10, padding: "2px 6px" }}
          onClick={() => {
            navigator.clipboard.writeText(token);
            toast.success("Token copied to clipboard");
          }}
        >
          📋 Copy
        </Button>
      </div>
    </div>
  );
};

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: preact.ComponentChildren;
};

export const Modal = ({ open, onClose, title, children }: ModalProps) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          width: "100%",
          maxWidth: 460,
          maxHeight: "90vh",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-muted)",
              fontSize: 22,
              cursor: "pointer",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            &times;
          </button>
        </div>
        <div style={{ padding: "16px 20px 20px" }}>{children}</div>
      </div>
    </div>
  );
};

type DialogSpec =
  | {
    kind: "alert";
    title: string;
    message: string;
    okLabel: string;
    resolve: () => void;
  }
  | {
    kind: "confirm";
    title: string;
    message: string;
    okLabel: string;
    cancelLabel: string;
    danger: boolean;
    resolve: (ok: boolean) => void;
  }
  | {
    kind: "prompt";
    title: string;
    message?: string;
    defaultValue: string;
    placeholder?: string;
    okLabel: string;
    cancelLabel: string;
    helperText?: string;
    inputMode?: "text" | "email" | "tel" | "url" | "numeric";
    resolve: (value: string | null) => void;
  }
  | {
    kind: "select";
    title: string;
    message?: string;
    options: SelectOption[];
    defaultValue?: string;
    okLabel: string;
    cancelLabel: string;
    helperText?: string;
    searchable: boolean;
    resolve: (value: string | null) => void;
  };

export type SelectOption = {
  value: string;
  label: string;
  description?: string;
  trailing?: string;
};

const dialogListeners = new Set<(d: DialogSpec | null) => void>();
const dialogState: { current: DialogSpec | null } = { current: null };

const setCurrentDialog = (d: DialogSpec | null) => {
  dialogState.current = d;
  dialogListeners.forEach((l) => l(d));
};

type AlertOptions = {
  title?: string;
  okLabel?: string;
};

export const alertDialog = (
  message: string,
  options: AlertOptions = {},
): Promise<void> =>
  new Promise((resolve) =>
    setCurrentDialog({
      kind: "alert",
      title: options.title ?? "Notice",
      message,
      okLabel: options.okLabel ?? "OK",
      resolve,
    })
  );

type ConfirmOptions = {
  title?: string;
  okLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

export const confirmDialog = (
  message: string,
  options: ConfirmOptions = {},
): Promise<boolean> =>
  new Promise((resolve) =>
    setCurrentDialog({
      kind: "confirm",
      title: options.title ?? "Confirm",
      message,
      okLabel: options.okLabel ?? "OK",
      cancelLabel: options.cancelLabel ?? "Cancel",
      danger: options.danger ?? false,
      resolve,
    })
  );

type PromptOptions = {
  title?: string;
  message?: string;
  defaultValue?: string;
  placeholder?: string;
  okLabel?: string;
  cancelLabel?: string;
  helperText?: string;
  inputMode?: "text" | "email" | "tel" | "url" | "numeric";
};

export const promptDialog = (
  label: string,
  options: PromptOptions = {},
): Promise<string | null> =>
  new Promise((resolve) =>
    setCurrentDialog({
      kind: "prompt",
      title: options.title ?? label,
      message: options.message ?? label,
      defaultValue: options.defaultValue ?? "",
      placeholder: options.placeholder,
      okLabel: options.okLabel ?? "OK",
      cancelLabel: options.cancelLabel ?? "Cancel",
      helperText: options.helperText,
      inputMode: options.inputMode,
      resolve,
    })
  );

type SelectOptions = {
  title?: string;
  message?: string;
  defaultValue?: string;
  okLabel?: string;
  cancelLabel?: string;
  helperText?: string;
  searchable?: boolean;
};

export const selectDialog = (
  options: SelectOption[],
  config: SelectOptions = {},
): Promise<string | null> =>
  new Promise((resolve) =>
    setCurrentDialog({
      kind: "select",
      title: config.title ?? "Choose an option",
      message: config.message,
      options,
      defaultValue: config.defaultValue,
      okLabel: config.okLabel ?? "Continue",
      cancelLabel: config.cancelLabel ?? "Cancel",
      helperText: config.helperText,
      searchable: config.searchable ?? options.length > 8,
      resolve,
    })
  );

const useDialog = () => {
  const [dialog, setDialog] = useState<DialogSpec | null>(dialogState.current);
  useEffect(() => {
    dialogListeners.add(setDialog);
    return () => {
      dialogListeners.delete(setDialog);
    };
  }, []);
  return dialog;
};

const dialogButtonRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 16,
};

const dialogMessageStyle = {
  whiteSpace: "pre-wrap",
  fontSize: 14,
  lineHeight: 1.5,
  color: "var(--text)",
};

const dialogHelperStyle = {
  fontSize: 12,
  lineHeight: 1.5,
  color: "var(--text-muted)",
  marginTop: 10,
  padding: "8px 10px",
  background: "var(--surface-alt)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
};

const dialogInputStyle = {
  width: "100%",
  padding: "8px 12px",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text)",
  fontSize: 14,
  marginTop: 10,
  boxSizing: "border-box" as const,
};

const AlertDialogBody = (
  { dialog }: { dialog: Extract<DialogSpec, { kind: "alert" }> },
) => (
  <div>
    <div style={dialogMessageStyle}>{dialog.message}</div>
    <div style={dialogButtonRowStyle}>
      <Button
        className="primary"
        onClick={() => closeDialog(() => dialog.resolve())}
      >
        {dialog.okLabel}
      </Button>
    </div>
  </div>
);

const ConfirmDialogBody = (
  { dialog }: { dialog: Extract<DialogSpec, { kind: "confirm" }> },
) => (
  <div>
    <div style={dialogMessageStyle}>{dialog.message}</div>
    <div style={dialogButtonRowStyle}>
      <Button
        className="secondary"
        onClick={() => closeDialog(() => dialog.resolve(false))}
      >
        {dialog.cancelLabel}
      </Button>
      <Button
        className="primary"
        style={dialog.danger
          ? { background: "var(--error)", borderColor: "var(--error)" }
          : undefined}
        onClick={() => closeDialog(() => dialog.resolve(true))}
      >
        {dialog.okLabel}
      </Button>
    </div>
  </div>
);

const PromptDialogBody = (
  { dialog }: { dialog: Extract<DialogSpec, { kind: "prompt" }> },
) => {
  const [value, setValue] = useState(dialog.defaultValue);
  const submit = () => closeDialog(() => dialog.resolve(value));
  const cancel = () => closeDialog(() => dialog.resolve(null));
  return (
    <div>
      {dialog.message && <div style={dialogMessageStyle}>{dialog.message}</div>}
      <input
        type="text"
        autoFocus
        value={value}
        placeholder={dialog.placeholder}
        inputMode={dialog.inputMode}
        onInput={(e) => setValue(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        style={dialogInputStyle}
      />
      {dialog.helperText && (
        <div style={dialogHelperStyle}>{dialog.helperText}</div>
      )}
      <div style={dialogButtonRowStyle}>
        <Button className="secondary" onClick={cancel}>
          {dialog.cancelLabel}
        </Button>
        <Button className="primary" onClick={submit}>
          {dialog.okLabel}
        </Button>
      </div>
    </div>
  );
};

const closeDialog = (after: () => void) => {
  setCurrentDialog(null);
  after();
};

const SelectDialogBody = (
  { dialog }: { dialog: Extract<DialogSpec, { kind: "select" }> },
) => {
  const [value, setValue] = useState<string | null>(
    dialog.defaultValue ?? null,
  );
  const [query, setQuery] = useState("");
  const submit = () =>
    value !== null && closeDialog(() => dialog.resolve(value));
  const cancel = () => closeDialog(() => dialog.resolve(null));
  const filtered = dialog.searchable && query.trim()
    ? dialog.options.filter((o) => {
      const q = query.toLowerCase();
      return o.label.toLowerCase().includes(q) ||
        (o.description ?? "").toLowerCase().includes(q);
    })
    : dialog.options;
  return (
    <div>
      {dialog.message && <div style={dialogMessageStyle}>{dialog.message}</div>}
      {dialog.searchable && (
        <input
          type="text"
          autoFocus
          value={query}
          placeholder="Search..."
          onInput={(e) => setQuery(e.currentTarget.value)}
          style={{ ...dialogInputStyle, marginBottom: 8 }}
        />
      )}
      <div
        style={{
          maxHeight: 320,
          overflowY: "auto",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          background: "var(--surface-alt)",
        }}
      >
        {filtered.length === 0
          ? (
            <div
              style={{
                padding: 16,
                textAlign: "center",
                color: "var(--text-muted)",
                fontSize: 14,
              }}
            >
              No matches
            </div>
          )
          : filtered.map((opt) => {
            const selected = value === opt.value;
            return (
              <div
                key={opt.value}
                onClick={() => setValue(opt.value)}
                onDblClick={() => closeDialog(() => dialog.resolve(opt.value))}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "10px 12px",
                  cursor: "pointer",
                  background: selected
                    ? "rgba(120, 160, 255, 0.15)"
                    : "transparent",
                  borderLeft: selected
                    ? "3px solid var(--primary)"
                    : "3px solid transparent",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 14,
                      color: "var(--text)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {opt.label}
                  </div>
                  {opt.description && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--text-muted)",
                        marginTop: 2,
                      }}
                    >
                      {opt.description}
                    </div>
                  )}
                </div>
                {opt.trailing && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--text-muted)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {opt.trailing}
                  </div>
                )}
              </div>
            );
          })}
      </div>
      {dialog.helperText && (
        <div style={dialogHelperStyle}>{dialog.helperText}</div>
      )}
      <div style={dialogButtonRowStyle}>
        <Button className="secondary" onClick={cancel}>
          {dialog.cancelLabel}
        </Button>
        <Button
          className="primary"
          onClick={submit}
          style={value === null
            ? { opacity: 0.5, pointerEvents: "none" }
            : undefined}
        >
          {dialog.okLabel}
        </Button>
      </div>
    </div>
  );
};

const renderDialogBody = (dialog: DialogSpec) => {
  if (dialog.kind === "alert") return <AlertDialogBody dialog={dialog} />;
  if (dialog.kind === "confirm") return <ConfirmDialogBody dialog={dialog} />;
  if (dialog.kind === "select") return <SelectDialogBody dialog={dialog} />;
  return <PromptDialogBody dialog={dialog} />;
};

const dialogCancelOnClose = (dialog: DialogSpec) => () => {
  if (dialog.kind === "alert") closeDialog(() => dialog.resolve());
  else if (dialog.kind === "confirm") closeDialog(() => dialog.resolve(false));
  else closeDialog(() => dialog.resolve(null));
};

export const DialogHost = () => {
  const dialog = useDialog();
  if (!dialog) return null;
  return (
    <Modal
      open
      onClose={dialogCancelOnClose(dialog)}
      title={dialog.title}
    >
      {renderDialogBody(dialog)}
    </Modal>
  );
};

export const formatDate = (
  dateOrMs: number | Date | string | undefined | null,
): string => {
  if (!dateOrMs) return "";
  const date = typeof dateOrMs === "number" || typeof dateOrMs === "string"
    ? new Date(dateOrMs)
    : dateOrMs;
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export const formatDateOnly = (
  dateOrMs: number | Date | string | undefined | null,
): string => {
  if (!dateOrMs) return "";
  const date = typeof dateOrMs === "number" || typeof dateOrMs === "string"
    ? new Date(dateOrMs)
    : dateOrMs;
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const formatPhone = (
  phoneNumber: string | undefined | null,
): string => {
  if (!phoneNumber) return "";
  try {
    const parsed = parsePhoneNumber("+" + phoneNumber.replace(/^\+/, ""));
    if (parsed) {
      return parsed.formatInternational();
    }
  } catch {
    // fallback
  }
  return phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;
};
